import type {
  MetricCatalogMetricEntry,
  MetricCatalogModuleEntry,
  MetricCatalogResponse,
} from "@/interfaces/v2Analytics";

/**
 * Nested Recommends breakdown keys (`ai_recommends_overall`, …). The parent
 * `ai_recommends` stays selectable; children only appear as card sub-metrics
 * from the analytics response, never as catalog picker / request metrics.
 */
export function isAiRecommendsSubMetric(metricKey: string): boolean {
  return metricKey.toLowerCase().startsWith("ai_recommends_");
}

type RawCatalog = {
  modules?: MetricCatalogModuleEntry[];
};

/**
 * Maps the catalog API payload into the store shape:
 * - keeps `modules` only (ignores root `aiVisibility` models/categories)
 * - for `ai_visibility`, drops nested Recommends sub-metrics
 * - fills missing metric fields so entries match `MetricCatalogMetricEntry`
 */
export function sanitizeMetricsCatalog(
  raw: MetricCatalogResponse | null,
): MetricCatalogResponse | null {
  if (!raw) return null;

  const modules = ((raw as RawCatalog).modules ?? []).map((mod) => {
    if (mod.moduleKey?.toLowerCase() !== "ai_visibility") return mod;
    return {
      moduleKey: mod.moduleKey,
      label: mod.label,
      ...(mod.labelOverrides ? { labelOverrides: mod.labelOverrides } : {}),
      metrics: (mod.metrics ?? [])
        .filter((metric) => !isAiRecommendsSubMetric(metric.metricKey))
        .map(toCatalogMetricEntry),
    };
  });

  return { modules };
}

/** Put thin AI Visibility API rows into the catalog metric contract. */
function toCatalogMetricEntry(
  metric: Partial<MetricCatalogMetricEntry> & {
    metricKey: string;
    label: string;
  },
): MetricCatalogMetricEntry {
  return {
    metricKey: metric.metricKey,
    label: metric.label,
    ...(metric.labelOverrides ? { labelOverrides: metric.labelOverrides } : {}),
    ...(metric.questionText != null ? { questionText: metric.questionText } : {}),
    ...(metric.questionTextOverrides
      ? { questionTextOverrides: metric.questionTextOverrides }
      : {}),
    ...(metric.description != null ? { description: metric.description } : {}),
    ...(metric.descriptionOverrides
      ? { descriptionOverrides: metric.descriptionOverrides }
      : {}),
    ...(metric.questionCode != null ? { questionCode: metric.questionCode } : {}),
    defaultScoreType: metric.defaultScoreType ?? "PERCENTAGE",
    marketIds: metric.marketIds ?? [],
    scoreTypes: metric.scoreTypes ?? {
      PERCENTAGE: { label: "Percentage" },
    },
  };
}
